/*
 * Authors: mariushirjanu
 * Created: 30/06/2020
 * Upgraded: 21/02/2023
 */
import * as _ from 'underscore';
import { Subscription } from 'rxjs';
import { trackByFunction } from '../../../shared/formly/ga-formly.util';
import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ICartItemStatus, ICatalogCartItem, IPricesResponse, IPunchoutCartItem, PriceCartService } from './price-cart.service';
import { IOrderFee } from './price-cart-data.service';
import { ICurrency, IPrice } from '../../../../type_definitions/datatypes';

import { PartConfiguratorService } from './part-configurator.service';
import { ICatalogProduct, IProductSearchList, OrderCatalogDataService } from '../catalog/order-catalog-data.service';
import { CatalogStateService } from '../catalog/catalog-state.service';
import { IOrderContext, OrderEntryDataService, ProcurementType } from '../entry/order-entry-data.service';
import { OrderEntryWebshopService } from '../entry/webshop/order-entry-webshop.service';
import { FormattingService } from '../../../shared/services/formatting.service';
import { OrderCatalogService } from '../catalog/order-catalog.service';
import { GaMessageService, IDialogButton } from '../../../shared/message/ga-message.service';
import { TranslationService } from '../../../shared/translation/translation.service';
import { EVENT_ORDER_CART_CURRENCY_CHANGED, EVENT_ORDER_CART_FEE_CHANGED, EVENT_ORDER_CART_PRICE_CHANGED, EVENT_ORDER_CART_STATUS_UPDATED, EVENT_ORDER_CONTEXT_UPDATED } from '../../../shared/app-events';
import { GaEventService, IGAEvent } from '../../../shared/services/ga-event.service';
import { OrderEntryService } from '../entry/order-entry.service';
import { util } from '../../../shared/util';
import { GaDeferredService, IGADeferred } from '../../../shared/services/ga-deferred.service';
import { IValidatingComponent } from '../../../shared/formly/ga-validate.service';
import { ICatalogGuideCodePart } from '../../webshop/guide/webshop-guide-data.service';

interface ICompatibleSection {
  id:number;
  name:string;
  initializing:boolean;
  loading:boolean;
  overview: {
    products:ICatalogProduct[]|ICatalogGuideCodePart[];
    compatibleProductId?:number;
    cartAction?:Function;
    hideHeader?:boolean;
    noStatus?:boolean;
    busyDisabled?:boolean;
    showStock?:boolean;
  }
}

@Component({
  selector: 'cart',
  template: `
    <div class="d-flex">
      <h2 class="cart-title">
        <span translate>{{title}}</span>
        <button class="btn btn-sm btn-success" *ngIf="hasPriceWarnings" (click)="acceptPriceChangesDialog()" translate>ORDERENTRY.CART.ACCEPT_PRICE</button>
      </h2>

      <div class="fees-currency-wrap ms-auto ps-2">
        <div class="cartfees" *ngIf="fees">
          <div class="fee" *ngIf="fees.lamFee">
            <span class="feelabel"><span translate>ORDERENTRY.CART.LAM_FEE</span>:</span>
            <span>{{formatAmount(fees.lamFee)}}</span>
          </div>
          <div class="separator" *ngIf="fees.lamFee && fees.acslFee"></div>
          <div class="fee" *ngIf="fees.acslFee">
            <span class="feelabel"><span translate>ORDERENTRY.CART.ACSL_FEE</span>:</span>
            <span>{{formatAmount(fees.acslFee)}}</span>
          </div>
        </div>

        <label class="currency-picker" *ngIf="customerCurrencies && !hidePrices">
          <select class="form-select form-select-sm input-sm" [(ngModel)]="orderCurrency" (change)="onCurrencyChange()" required="required">
            <option *ngFor="let currency of customerCurrencies; trackBy: trackByFunction" [ngValue]="currency">{{ currency.description }}</option>
          </select>
        </label>
      </div>
    </div>

    <div class="alert spend-limit-section" [ngClass]="{ 'alert-danger': prices.spendLimitExceeded, 'alert-success': !prices.spendLimitExceeded }" *ngIf="prices?.spendLimit && !isPopupCart">
      <p class="mb-0">
        <i class="fa fa-info-circle info-spend-limit" [ngClass]="{ 'text-danger': prices.spendLimitExceeded, 'text-success': !prices.spendLimitExceeded }"></i>
        <span translate [translateParams]="{
          spendLimit: formatAmount(prices.spendLimit, prices.spendLimitCurrency),
          remaining: formatAmount(prices.remainingAmountFromSpendLimit, prices.spendLimitCurrency) }">
          {{ prices.spendLimitExceeded? 'ORDERENTRY.CART.SPEND_LIMIT_EXCEEDED' : 'ORDERENTRY.CART.SPEND_LIMIT_OK' }}
        </span>
      </p>
    </div>

    <div *ngIf="showProposeAlternatives && !isPopupCart && !punchout">
      <propose-alternatives></propose-alternatives>
    </div>

    <table class="table table-striped cart" [ngClass]="{'cart-with-warnings': hasWarnings}">
      <thead>
      </thead>
      <tbody>
        <tr *ngFor="let item of cartStatus; trackBy: trackByFunction">
          <ng-container *ngIf="item.product">
            <td class="part">
              <a [uiSref]="detailState" [uiParams]="{ product: item.id }" class="badge bg-info has-hover" *ngIf="!item.product.inValid && item.id">
                {{item.product.part}}<span *ngIf="item.product.partSuffix">#{{item.product.partSuffix}}</span>
              </a>
              <span class="badge bg-info has-hover" *ngIf="item.product.inValid || !item.id">{{item.product.part}}</span>
            </td>
            <td [ngClass]="isPopupCart ? 'title-popup' : 'title'">
              <info-link *ngIf="item?.product?.description" [(linkVariable)]="item.showInfo"></info-link>
              <a [uiSref]="detailState" [uiParams]="{ product: item.id }" *ngIf="!item.product.inValid && item.id">{{item.product.shortDescription}}</a>
              <span *ngIf="!item.product.inValid && !item.id">{{item.product.title}}</span>
              <span *ngIf="item.product.inValid">{{item.product?.shortDescription}}</span>
              <div class="info-row" *ngIf="item.showInfo">{{item.product.description}}</div>
              <div class="lead-time" *ngIf="item.product?.leadTime?.text" [ngClass]="item.product?.leadTime?.warning? 'warn' : 'no-warn'">
                <i class="fa fa-warning" *ngIf="item.product?.leadTime?.warning"></i>
                <span translate [translateParams]="{days: item.product?.leadTime?.text}">CATALOG.DETAIL.LEAD_TIME</span>
              </div>
              <div class="part-configuration" *ngIf="item.product.partSuffixFullDescription">
                {{item.product.partSuffixFullDescription}} &nbsp;
                <a href="" (click)="editPartConfiguration($event, item)"><i class="fa fa-pencil"></i></a>
              </div>
            </td>
            <td class="add-to-cart-wrap">
              <add-to-cart [product]="item.product" (cartAction)="addToCart($event)" [catalogView]="false" [mini]="true"></add-to-cart>
              <button class="remove-from-cart btn btn-sm btn-danger" (click)="removeFromCart(item.product)">
                <i class="fa fa-remove"></i>
              </button>
            </td>
            <td class="lease-period" *ngIf="showOnlyLeasingPrices">
              <span translate [translateParams]="{ months: item.product.leasePeriod }" *ngIf="item.product.leasePeriod">CATALOG.MONTHS_LABEL</span>
            </td>
            <td class="price lease-price-monthly bold" *ngIf="showOnlyLeasingPrices" [ngClass]="{strike: item.product.inValid}">
              <div *ngIf="item.product.unitLeasePrice">{{ getLeasePrice(item.product.unitLeasePrice) }}</div>
            </td>
            <td class="price" [ngClass]="{strike: item.product.inValid}">
              <ng-container *ngIf="!showOnlyLeasingPrices">
                <div *ngIf="item.orderLineStatus === 'NEWPRICE'" class="strike">{{ formatAmount(item.product.initialUnitPrice) }}</div>
                <div *ngIf="item.product?.price?.amount">{{ formatAmount(item.product.price.amount) }}</div>
              </ng-container>

              <ng-container *ngIf="showOnlyLeasingPrices">
                <div *ngIf="item.product.unitLeasePriceTotal">{{ getLeasePrice(item.product.unitLeasePriceTotal) }}</div>
              </ng-container>
            </td>
            <td class="warnings errorText" *ngIf="hasWarnings">
              <div *ngIf="item.warning"><i class="fa fa-warning"></i> {{item.warning.text}} <a href
                (click)="item.warning?.popUp($event)" translate>GA.GENERAL.SEE_MORE</a></div>
            </td>
          </ng-container>
        </tr>
      </tbody>
      <tfoot *ngIf="showTotals">
        <tr>
          <td class="empty" colspan="2"></td>
          <td class="pricelabel"><i *ngIf="loading" class="fa fa-spinner fa-spin"></i>&nbsp;<span translate>ORDERENTRY.CART.SUBTOTAL</span>:</td>
          <td class="pricelabel" *ngIf="showOnlyLeasingPrices"></td>
          <td class="price price-lease bold" *ngIf="showOnlyLeasingPrices">{{ formatAmount(prices.leasePriceTotals?.monthly?.subtotal) }}</td>
          <td class="price price-subtotal">{{formatAmount(showOnlyLeasingPrices ? prices.leasePriceTotals?.total?.subtotal : prices.purchasePriceTotals?.subtotal)}}</td>
          <td *ngIf="hasWarnings"></td>
        </tr>
        <tr *ngIf="showOnlyLeasingPrices ? prices.leasePriceTotals?.total?.chargesTotal : prices.purchasePriceTotals?.chargesTotal">
          <td class="empty" colspan="2"></td>
          <td class="pricelabel"><i *ngIf="loading" class="fa fa-spinner fa-spin"></i>&nbsp;<span translate>ORDERENTRY.CART.CHARGES</span>:</td>
          <td class="pricelabel" *ngIf="showOnlyLeasingPrices"></td>
          <td class="price price-lease text-end bold" *ngIf="showOnlyLeasingPrices">{{ formatAmount(prices.leasePriceTotals?.monthly?.chargesTotal) }}</td>
          <td class="price charges-total">{{formatAmount(showOnlyLeasingPrices ? prices.leasePriceTotals?.total?.chargesTotal : prices.purchasePriceTotals?.chargesTotal)}}</td>
          <td *ngIf="hasWarnings"></td>
        </tr>
        <tr>
          <td class="empty" colspan="2"></td>
          <td class="pricelabel"><i *ngIf="loading" class="fa fa-spinner fa-spin"></i>&nbsp;<span translate>ORDERENTRY.CART.VAT_TAXES</span>:</td>
          <td class="pricelabel" *ngIf="showOnlyLeasingPrices"></td>
          <td class="price price-lease bold" *ngIf="showOnlyLeasingPrices">{{ formatAmount(prices.leasePriceTotals?.monthly?.tax) }}</td>
          <td class="price price-tax">{{formatAmount(showOnlyLeasingPrices ? prices.leasePriceTotals?.total?.tax : prices.purchasePriceTotals?.tax)}}</td>
          <td *ngIf="hasWarnings"></td>
        </tr>
        <tr>
          <td class="empty" colspan="2"></td>
          <td class="pricelabel"><i *ngIf="loading" class="fa fa-spinner fa-spin"></i>&nbsp;<span translate>ORDERENTRY.CART.TOTAL</span>:</td>
          <td class="pricelabel" *ngIf="showOnlyLeasingPrices"></td>
          <td class="price price-lease bold" *ngIf="showOnlyLeasingPrices">{{ formatAmount(prices.leasePriceTotals?.monthly?.total) }}</td>
          <td class="price price-total">{{formatAmount(showOnlyLeasingPrices ? prices.leasePriceTotals?.total?.total : prices.purchasePriceTotals?.total)}}</td>
          <td *ngIf="hasWarnings"></td>
        </tr>
      </tfoot>
    </table>

    <div *ngIf="hasCompatibleProducts()">
      <h2>
        <span translate>ORDERENTRY.CART.COMPATIBLES</span>
      </h2>

      <div *ngFor="let section of compatibleSections; trackBy: trackByFunction">
        <collapsible-pane [title]="section.name" (onChange)="loadCompatibles(section, $event)">
          <div class="collapsible-title-extra">
            <span *ngIf="section.loading" class="col-margin-left">
              <activity-indicator></activity-indicator>
            </span>
          </div>
          <div *ngIf="!section.loading && section.overview.products.length">
            <catalog-overview-layout-tabularbasic [products]="section.overview.products" [hideHeader]="section.overview?.hideHeader" [noStatus]="section.overview?.noStatus" [compatibleProductId]="section.overview?.compatibleProductId" [showStock]="section.overview?.showStock" [hasCartAction]="!!section.overview?.cartAction"></catalog-overview-layout-tabularbasic>
          </div>
          <div class="warnings errorText" *ngIf="!section.loading && !section.overview.products.length">
            <span translate>CATALOG.NO_PRODUCTS</span>
          </div>
        </collapsible-pane>
      </div>

    </div>
  `
})
export class CartComponent implements IValidatingComponent, OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() public isPopupCart:boolean;
  @Input() public punchout:boolean;
  @Input() public orderId:number;

  public title:string;
  public loading:boolean;
  public cartStatus:ICartItemStatus[];
  public prices:IPricesResponse;
  public fees:IOrderFee;
  public compatibleSections:ICompatibleSection[] = [];
  public hasWarnings:boolean;
  public hasPriceWarnings:boolean;
  public hidePrices:boolean;
  public customerCurrencies:ICurrency[];
  public orderCurrency:ICurrency;
  public showProposeAlternatives:boolean;
  public trackByFunction:Function = trackByFunction;
  public detailState:string;
  public showLease:boolean;

  private merged:boolean = false;
  private eventHandler:Subscription;
  private shopContext:IOrderContext;
  private onValidationReadyDeferred:IGADeferred<void>;

  constructor(
    private eventService:GaEventService,
    private messageService:GaMessageService,
    private priceCartService:PriceCartService,
    private partConfiguratorService:PartConfiguratorService,
    private translationService:TranslationService,
    private orderCatalogDataService:OrderCatalogDataService,
    private catalogStateService:CatalogStateService,
    private orderDataService:OrderEntryDataService,
    private orderEntryService:OrderEntryService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private formattingService:FormattingService,
    private orderCatalogService:OrderCatalogService,
    deferredService:GaDeferredService
  ) {
    this.onValidationReadyDeferred = deferredService.defer();
  }

  public ngOnInit():void {
    this.activate();
    this.listen();
  }

  public ngAfterViewInit():void {
    this.onValidationReadyDeferred.resolve();
  }

  private activate():void {
    this.detailState = this.catalogStateService.detailState;
    this.title = 'ORDERENTRY.CART.TITLE';
    this.showProposeAlternatives = this.orderCatalogService.showProposeAlternatives;
    this.shopContext = _.clone(this.orderEntryWebshopService.shopContext);
    this.showLease = this.shopContext?.defaultProcurementType === ProcurementType.LEASE;
    this.setCurrencies();
    this.prices = <IPricesResponse>this.priceCartService.prices;
    if(!this.isPopupCart){
      this.loadPrices(); // necessary for showing the cart limit in order shopping cart tab
    }
    this.fees = this.priceCartService.fees;
    this.updateFees();
    this.updateCartStatus();
    this.setupCompatibleSections();
    this.initInfo();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if(changes.isPopupCart){
      this.isPopupCart = changes.isPopupCart.currentValue;
      this.loadPrices();
    }
  }

  public get showOnlyLeasingPrices():boolean {
    return this.showLease && this.hidePrices;
  }

  public get showTotals():boolean {
    return (!this.hidePrices || this.showLease) && !!this.prices;
  }

  public getLeasePrice(price:IPrice|number):string {
    return this.formatAmount(_.isObject(price) ? (<IPrice>price)?.amount : <number>price);
  }

  private listen():void {
    this.eventHandler = this.eventService.events.subscribe((event:IGAEvent) => {
      switch(event.name){
        case EVENT_ORDER_CONTEXT_UPDATED:
          this.setCurrencies(event.data);
          this.updateFees(event.data);
        break;
        
        case EVENT_ORDER_CART_PRICE_CHANGED:
          this.prices = event.data;
          this.updateFees();
        break;

        case EVENT_ORDER_CART_FEE_CHANGED:
          this.fees = event.data;
        break;

        case EVENT_ORDER_CART_CURRENCY_CHANGED:
          if(!this.orderCurrency || event.data !== this.orderCurrency?.isoCode) {
            this.loadPrices();
            this.syncCurrency(event.data);
          }
          break;

        case EVENT_ORDER_CART_STATUS_UPDATED:
          this.updateCartStatus(event.data);
        break;
      }
    });
  }

  private syncCurrency(newCurrency:string):void {
    // sync currency dropdown selector from the cart popup with the one from cart tab from orderentry
    if(this.customerCurrencies){
      this.orderCurrency = _.find(this.customerCurrencies, (currency:ICurrency) => currency.isoCode === newCurrency);
    }
  }

  public formatAmount(price:number, currency?:string):string {
    return this.formattingService.formatAmount(price, currency || this.orderCurrency);
  }

  public addToCart(item:{product:ICatalogCartItem | IPunchoutCartItem; count:number}):Promise<number> {
    return this.priceCartService.addToCart(item.product, item.count)
      .then((id:number) => {
        this.loadPrices();
        return id;
      });
  }

  public removeFromCart(product:ICatalogCartItem | IPunchoutCartItem):void {
    this.priceCartService.removeFromCart(product)
      .then(() => {
        this.removeCompatibleSection(product);
        this.loadPrices();
      });
  }

  // called when the edit part configuration button is clicked
  public editPartConfiguration(event:Event, item:ICartItemStatus):void {
    event.preventDefault();
    this.partConfiguratorService.selectPart(<ICatalogCartItem>item.product, Number(item.count))
      .then(() => {
        // remove the existing product from the cart as itemId can change
        this.priceCartService.removeFromCart(item.product);
        // add product to the cart
        item.product.itemId = undefined;
        this.priceCartService.addToCart(item.product, item.count);
      });
  }

  public acceptPriceChangesDialog():void {
    const title:string = 'Accept Price Changes';
    const message:string =
      `By clicking 'Accept', you accept all price changes in your shopping cart.<br />
      If you do not agree with a new price, you can remove the item from your cart.`;
    const buttons:IDialogButton[] = [
      {
        label: 'Accept',
        className: 'btn btn-success',
        id: 'accept'
      },
      {
        label: 'Cancel'
      }
    ];
    const callback = arg => {
      if (arg === 'accept') {
        this.acceptPriceChanges();
      }
    };

    this.messageService.showMessageDialog(title, message, buttons, callback, undefined, undefined, false);
  }

  public loadCompatibles(section:ICompatibleSection, collapsed:boolean):void {
    if (section.initializing && !collapsed) {
      if (_.findWhere(this.compatibleSections, { id: section.id })) {
        section.initializing = false;
        section.loading = true;
        this.orderCatalogDataService.search(section.id)
          .then((data:IProductSearchList) => {
            if (data.products) {
              section.overview.products = data.products;
              section.overview.cartAction = this.addToCart;
              section.overview.hideHeader = true;
              section.overview.noStatus = true;
              section.overview.busyDisabled = true;
            }
          })
          .finally(() => {
            section.loading = false;
          });

      }
    }
  }

  public hasCompatibleProducts():boolean {
    if (this.orderEntryService.order && !this.merged) {
      _.each(this.cartStatus, (itemStatus:ICartItemStatus) => this.mergeCompatibleOptions(itemStatus));
      this.merged = true;
    }

    const product:(ICatalogCartItem | IPunchoutCartItem)[] = _.map(this.cartStatus, (itemStatus:ICartItemStatus) => itemStatus.product);
    return (!this.isPopupCart && !!_.findWhere(product, {
      compatibleProducts: true,
      isMachine: true
    }));
  }

  public beforeValidate = ():Promise<any> => {
    return this.onValidationReadyDeferred.promise;
  };

  public validate = ():Promise<void|string[]> => {
    const errors:string[] = [];
    if (this.hasWarnings) {
      errors.push(this.translationService.instant('ORDERENTRY.CART.NOT_UP_TO_DATE'));
    }

    if (!this.priceCartService.cartCount) {
      errors.push(this.translationService.instant('ORDERENTRY.CART.EMPTY.MESSAGE'));
    }

    // currency warnings
    if (this.priceCartService.currency !== this.orderEntryWebshopService.shopContext.defaultOrderCurrency.isoCode && !this.orderEntryWebshopService.shopContext.currencyChangeAllowed) {
      errors.push(this.translationService.instant('ORDERENTRY.CONFIRM.WARN_CURRENCY_WRONG'));
    }

    return util.messagesToPromise(errors);
  };

  public loadPrices():void {
    this.loading = true;
    setTimeout(() => { // give some time to display the spinner
      const orderId:number = this.orderEntryService.order?.id;
      this.priceCartService.load(orderId)
        .then((prices:IPricesResponse) => {
          if (prices?.items) {
            _.forEach(prices.items, (item:any) => {
              const itemId = this.priceCartService.getItemId(item);
              const cartItem:any = _.findWhere(this.cartStatus, { itemId });
              if (cartItem) {
                cartItem.product.unitPrice = item.unitPrice;
                cartItem.product.initialLeasePrice = item.unitLeasePrice;
                cartItem.product.initialLeasePriceTotal = item.unitLeasePriceTotal;
                cartItem.product.price.amount = item.price;
              }
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    });

  }

  private setupCompatibleSections():void {
    if (this.hasCompatibleProducts()) {
      this.compatibleSections = [];
      _.each(this.cartStatus, (cartItem:ICartItemStatus) => this.createSection(cartItem));
    }
  }

  private createSection(cartItem:ICartItemStatus):void {
    const product:ICatalogCartItem | IPunchoutCartItem = cartItem.product;
    if (product.isMachine && product.compatibleProducts) {
      this.compatibleSections.push(
        {
          id: product.id,
          name: `${product.part} ${product.shortDescription}`,
          initializing: true,
          loading: false,
          overview: {
            products: []
          }
        });
    }
  }

  private initInfo():void {
    _.each(this.cartStatus, (c:ICartItemStatus) => c.showInfo = false);
  }

  private removeCompatibleSection(product:ICatalogCartItem | IPunchoutCartItem):void {
    const indexCompatibles = _.indexOf(this.compatibleSections, _.find(this.compatibleSections, { id: product.id }));
    if (indexCompatibles >= 0) {
      this.compatibleSections.splice(indexCompatibles, 1);
    }
  }

  private acceptPriceChanges():void {
    this.orderDataService.itemsAction(this.orderId, 'CLEARWARNINGS')
      .then(() => {
        _.forEach(this.cartStatus, (p:any) => {
          const product = p.product;
          if (product.orderLineStatus === 'NEWPRICE') {
            delete product.orderLineStatus;
          }
        });
        this.priceCartService.cartStatus = this.cartStatus;
        this.hasWarnings = this.priceCartService.hasWarnings();
        this.hasPriceWarnings = this.priceCartService.hasPriceWarnings();
      });
  }

  private mergeCompatibleOptions(c:any):void {
    const orderItem:ICatalogCartItem = <ICatalogCartItem><any>_.findWhere(this.orderEntryService.order.items, { productId: c.product.id });
    if (orderItem) {
      const product:any = c.product;
      product.isMachine = orderItem.isMachine;
      product.compatibleProducts = orderItem.compatibleProducts;
    }
  }

  private setCurrencies(shopContext?:IOrderContext):void {
    shopContext = shopContext || this.shopContext;
    this.customerCurrencies = shopContext?.customerCurrencies;
    if (this.priceCartService.currency) {
      this.orderCurrency = _.findWhere(this.customerCurrencies, { isoCode: this.priceCartService.currency });
    }
    if (!this.orderCurrency) {
      this.orderCurrency = _.findWhere(this.customerCurrencies, { isoCode: shopContext?.defaultOrderCurrency });
    }
  }

  public onCurrencyChange():void {
    this.priceCartService.currency = this.orderCurrency.isoCode;
    this.orderCatalogService.currency = this.orderCurrency.isoCode;
    this.loadPrices();
    this.updateFees();
  }

  private updateCartStatus(cartStatus?:ICartItemStatus[]):void {
    cartStatus = cartStatus || this.priceCartService.cartStatus;

    // check for warnings
    if (this.orderEntryService.orderStatus !== 'PLACED') {
      this.hasPriceWarnings = this.priceCartService.hasPriceWarnings();
      this.hasWarnings = this.priceCartService.hasWarnings();
    } else {
      this.hasPriceWarnings = false;
      this.hasWarnings = false;
    }
    if(cartStatus){
      this.cartStatus = cartStatus;
    }
  }

  private updateFees(context?:IOrderContext):void {
    context = context || this.shopContext;

    if (this.orderCurrency) {
      if (this.orderId && (context.acslFeeDisplayAllowed || context.lamFeeDisplayAllowed)) {
        this.priceCartService.loadFees(this.orderId);
      }
    }

    this.hidePrices = context?.hidePrices;
  }

  public ngOnDestroy():void {
    this.eventHandler.unsubscribe();
  }
}